/* Native scroll choreography. No wheel interception, commerce or network writes. */
(() => {
  'use strict';
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const clamp = (value, min = 0, max = 1) => Math.min(max, Math.max(min, value));
  const ease = value => { const t = clamp(value); return t * t * (3 - 2 * t); };
  const between = (p, start, end) => ease((p - start) / (end - start));
  const chapterPositions = [0, .48, .86];

  function timeline(p) {
    p = clamp(p);
    const dial = between(p, .24, .36);
    const movement = between(p, .60, .72);
    return {
      opacity: [1 - dial, dial * (1 - movement), movement],
      chapter: p < .30 ? 0 : p < .66 ? 1 : 2,
      frontScale: 1 + p * .48,
      frontAngle: -14 + p * 16,
      frontX: p * 70,
      frontY: -p * 75,
      dialScale: 1.13 - clamp((p - .24) / .48) * .12,
      dialX: -clamp((p - .24) / .48) * 26,
      caliberAngle: -12 + clamp((p - .60) / .40) * 17,
      caliberScale: 1.05 + clamp((p - .60) / .40) * .12
    };
  }

  function init(root = document) {
    root.querySelectorAll('[data-watch-reveal]').forEach(story => {
      if (story.dataset.watchReady) return;
      story.dataset.watchReady = 'true';
      const stage = story.querySelector('[data-watch-stage]');
      const scenes = [...story.querySelectorAll('[data-watch-scene]')];
      const buttons = [...story.querySelectorAll('[data-watch-chapter]')];
      const navigation = story.querySelector('[data-watch-nav]');
      const cue = story.querySelector('[data-watch-cue]');
      const dialog = story.parentElement.querySelector('[data-watch-film-dialog]');
      const film = dialog?.querySelector('video');
      const openFilm = story.querySelector('[data-watch-film-open]');
      const closeFilm = dialog?.querySelector('[data-watch-film-close]');
      const soundControl = dialog?.querySelector('[data-watch-film-sound]');
      let enabled = false, frame = 0, lastChapter = -1, visible = true;
      const unbind = [];
      function listen(target, event, handler, options) {
        target?.addEventListener(event, handler, options);
        unbind.push(() => target?.removeEventListener(event, handler, options));
      }
      function geometry() {
        const top = parseFloat(getComputedStyle(story).getPropertyValue('--wr-top')) || 70;
        const rect = story.getBoundingClientRect();
        const distance = Math.max(1, story.offsetHeight - stage.offsetHeight);
        return { top, rect, distance };
      }
      function update() {
        frame = 0;
        if (!enabled) return;
        const { top, rect, distance } = geometry();
        const p = clamp((top - rect.top) / distance);
        const state = timeline(p);
        const s = story.style;
        s.setProperty('--wr-progress', p);
        s.setProperty('--wr-front-scale', state.frontScale);
        s.setProperty('--wr-front-angle', state.frontAngle + 'deg');
        s.setProperty('--wr-front-x', state.frontX + 'px');
        s.setProperty('--wr-front-y', state.frontY + 'px');
        s.setProperty('--wr-dial-scale', state.dialScale);
        s.setProperty('--wr-dial-x', state.dialX + 'px');
        s.setProperty('--wr-caliber-angle', state.caliberAngle + 'deg');
        s.setProperty('--wr-caliber-scale', state.caliberScale);
        s.setProperty('--wr-light', 190 + p * 100 + 'deg');
        s.setProperty('--wr-number-y', -p * 90 + 'px');
        s.setProperty('--wr-cue-opacity', 1 - between(p, 0, .10));
        scenes.forEach((scene, i) => scene.style.setProperty('--scene-opacity', state.opacity[i]));
        if (state.chapter !== lastChapter) {
          lastChapter = state.chapter;
          story.dataset.activeChapter = String(lastChapter);
          scenes.forEach((scene, i) => scene.setAttribute('aria-hidden', String(i !== lastChapter)));
          buttons.forEach((button, i) => {
            if (i === lastChapter) button.setAttribute('aria-current', 'true');
            else button.removeAttribute('aria-current');
          });
        }
      }
      function requestUpdate() {
        if (!frame && enabled && visible) frame = requestAnimationFrame(update);
      }
      function configure() {
        enabled = !reduced.matches && CSS.supports('position', 'sticky') && CSS.supports('overflow-x', 'clip');
        story.classList.toggle('is-enabled', enabled);
        navigation.hidden = false;
        cue.hidden = !enabled;
        if (!enabled) {
          if (frame) cancelAnimationFrame(frame);
          frame = 0;
          story.removeAttribute('style');
          scenes.forEach(scene => { scene.style.removeProperty('--scene-opacity'); scene.removeAttribute('aria-hidden'); });
          buttons.forEach(button => button.removeAttribute('aria-current'));
          lastChapter = -1;
          story.removeAttribute('data-active-chapter');
        } else update();
      }
      function goToChapter(index, behavior = 'smooth') {
        if (!enabled) { scenes[index].scrollIntoView({ behavior: 'auto', block: 'start' }); return; }
        const { top, rect, distance } = geometry();
        window.scrollTo({ top: window.scrollY + rect.top - top + chapterPositions[index] * distance, behavior });
      }
      buttons.forEach((button, index) => listen(button, 'click', () => goToChapter(index)));
      listen(window, 'scroll', requestUpdate, { passive: true });
      listen(window, 'resize', requestUpdate, { passive: true });
      listen(reduced, 'change', configure);
      listen(window, 'hashchange', () => {
        const index = scenes.findIndex(scene => '#' + scene.id === location.hash);
        if (index >= 0) goToChapter(index, 'auto');
      });
      listen(openFilm, 'click', () => {
        if (!dialog || !film) return;
        dialog.showModal();
        if (film.ended) film.currentTime = 0;
        film.play().then(() => {
          if (!dialog.open) film.pause();
        }).catch(() => { /* Native play control remains available. */ });
      });
      function updateSoundControl() {
        if (!soundControl || !film) return;
        const audible = !film.muted && film.volume > 0;
        soundControl.hidden = false;
        soundControl.setAttribute('aria-pressed', String(audible));
        soundControl.textContent = audible ? 'Sound on' : 'Sound off';
      }
      listen(soundControl, 'click', () => {
        if (film.muted || film.volume === 0) {
          film.muted = false;
          if (film.volume === 0) film.volume = 1;
        } else film.muted = true;
        updateSoundControl();
      });
      listen(film, 'volumechange', updateSoundControl);
      listen(document, 'visibilitychange', () => {
        if (document.hidden && dialog?.open) film?.pause();
      });
      updateSoundControl();
      listen(closeFilm, 'click', () => dialog.close());
      listen(dialog, 'close', () => { film.pause(); openFilm?.focus({ preventScroll: true }); });
      listen(dialog, 'click', event => {
        if (event.target !== dialog) return;
        const rect = dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) dialog.close();
      });
      let observer;
      if ('IntersectionObserver' in window) {
        observer = new IntersectionObserver(entries => {
          visible = entries[0].isIntersecting;
          if (visible) requestUpdate();
        }, { rootMargin: '200px 0px' });
        observer.observe(story);
      }
      configure();
      const hashIndex = scenes.findIndex(scene => '#' + scene.id === location.hash);
      if (hashIndex >= 0) goToChapter(hashIndex, 'auto');
      story._watchDispose = () => {
        if (frame) cancelAnimationFrame(frame);
        observer?.disconnect();
        unbind.forEach(off => off());
        if (dialog?.open) dialog.close();
        film?.pause();
      };
    });
  }
  init();
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => event.target.querySelectorAll('[data-watch-reveal]').forEach(story => story._watchDispose?.()));
})();
